import React, { useEffect, useRef, useState } from "react";
import * as d3 from "d3";
import Champ from "../images/Champ";
import useFetch from "../hooks/useFetch";
import { servers } from "../misc/Static";
import styles from "./MatchChart.module.css";

const chartTypes = {
  1: "gold",
  2: "teamgold",
  3: "teamgolddelta",
  4: "xp",
  5: "cs",
};
const chartColors = {
  1: "single",
  2: "team",
  3: "delta",
  4: "single",
  5: "single",
};
const widths = { xlarge: 970, large: 728, medium: 970, small: 728 };
const MatchChart = ({ chart, match, region, order, width }) => {
  const chartDiv = useRef(null);
  const graphtip = useRef(null);
  const [active, setActive] = useState("111111111111");
  const URL = `https://pp${servers[region]}.xdx.gg/chart/1/${region}/${match.mid}/`;
  const key = region + match.mid;
  const data = useFetch(URL, key)[key];
  const toggleChamp = (pid) => {
    let a =
      active.substring(0, pid) +
      (active.charAt(pid) === "0" ? "1" : "0") +
      active.substring(pid + 1);
    setActive(a);
  };
  useEffect(() => {
    setActive("111111111111");
  }, [chart]);

  useEffect(() => {
    const drawChart = () => {
      const type = chartTypes[chart];
      if (type === "teamgold") {
        data[type] = [[], []];
        order.forEach((team, i) => {
          team.forEach((pid, j) => {
            data["gold"][pid].forEach((gold, k) => {
              if (!data[type][i][k]) data[type][i][k] = 0;
              data[type][i][k] += gold;
            });
          });
        });
        if (!match.winner[0]) {
          let temp = data[type][0];
          data[type][0] = data[type][1];
          data[type][1] = temp;
        }
      }

      if (type === "teamgolddelta") {
        data[type] = [[], []];
        order.forEach((team, i) => {
          team.forEach((pid) => {
            data["gold"][pid].forEach((gold, k) => {
              if (!data[type][i][k]) data[type][i][k] = 0;
              if (!match.winner[0]) gold = -gold;
              if (i === 1) gold = -gold;
              data[type][0][k] += gold;
            });
          });
        });
        let temp = data[type][0];
        data[type][0] = data[type][1];
        data[type][1] = temp;
      }
      let converted = Object.keys(data[type]).map((n) =>
        data[type][n].map((y, x) => {
          return { x: x, y: y };
        })
      );
      let minY = 0,
        maxY = 0;

      Object.keys(data[type]).forEach((i) => {
        let max = Math.max(...data[type][i]);
        if (max > maxY) maxY = max;
        let min = Math.min(...data[type][i]);
        if (min < minY) minY = min;
      });
      let maxX = data[type][0].length - 1;
      const margin = { top: 20, right: 20, bottom: 50, left: 70 },
        chartWidth = widths[width] - margin.left - margin.right,
        height = 400 - margin.top - margin.bottom;
      const xScale = d3.scaleLinear().domain([0, maxX]).range([0, chartWidth]);
      const yScale = d3.scaleLinear().domain([minY, maxY]).range([height, 0]);
      const line = d3
        .line()
        .x(function (d, i) {
          return xScale(i);
        })
        .y(function (d) {
          return yScale(d.y);
        })
        .curve(d3.curveMonotoneX);
      if (chartDiv.current.firstElementChild)
        chartDiv.current.firstElementChild.remove();
      let svg = d3
        .select(chartDiv.current)
        .html("")
        .append("svg")
        .attr("width", chartWidth + margin.left + margin.right)
        .attr("height", height + margin.top + margin.bottom)
        .append("g")
        .attr("transform", "translate(" + margin.left + "," + margin.top + ")");
      svg
        .append("g")
        .attr("class", styles.x)
        .attr("transform", "translate(0," + height + ")")
        .call(d3.axisBottom(xScale));
      if (type === "cs") {
        svg.append("g").attr("class", styles.y).call(d3.axisLeft(yScale));
      } else {
        svg
          .append("g")
          .attr("class", styles.y)
          .call(d3.axisLeft(yScale).tickFormat(d3.formatPrefix(".1", 1e3)));
      }

      const titles = {
        gold: "Gold Earned",
        teamgold: "Team Gold Earned",
        teamgolddelta: "Team Gold Delta",
        xp: "Experience",
        cs: "Minons Killed",
      };

      svg
        .append("text")
        .attr("class", styles.axistitle)
        .attr("transform", "rotate(-90)")
        .attr("y", 0 - margin.left + 10)
        .attr("x", 0 - height / 2)
        .attr("dy", "1em")
        .style("text-anchor", "middle")
        .text(titles[type]);
      svg
        .append("text")
        .attr("class", styles.axistitle)
        .attr(
          "transform",
          "translate(" + chartWidth + " ," + (height + margin.bottom - 10) + ")"
        )
        .style("text-anchor", "end")
        .text("Time (minutes)");
      let tip = d3.select(graphtip.current).style("opacity", 0);
      const addLine = (n) => {
        svg
          .append("path")
          .datum(converted[n])
          .attr("class", styles["line_" + chartColors[chart] + n])
          .attr("d", line);
        svg
          .selectAll(".dot" + n)
          .data(converted[n])
          .enter()
          .append("circle")
          .attr("class", styles["dot_" + chartColors[chart] + n])
          .attr("cx", (d, i) => xScale(i))
          .attr("cy", (d) => yScale(d.y))
          .attr("r", 3)
          .on("mouseover", function (event, d) {
            d3.select(this).transition().duration("100").attr("r", 4);
            tip.transition().duration(200).style("opacity", 0.9);
            tip
              .html(d.y.toLocaleString() + "<br/>" + d.x + " min")
              .attr("class", styles["tip_" + chartColors[chart] + n])
              .style(
                "left",
                this.getBoundingClientRect().left + window.scrollX + 5 + "px"
              )
              .style(
                "top",
                this.getBoundingClientRect().top + window.scrollY - 37 + "px"
              );
          })
          .on("mouseout", function () {
            d3.select(this).transition().duration("100").attr("r", 3);
            tip.transition().duration(500).style("opacity", 0);
          });
      };
      for (var i = 0; i < converted.length; i++)
        if (active.charAt(i) === "1") addLine(i);
    };
    if (data) drawChart();
  }, [data, chart, order, match, active, width]);

  return (
    <div>
      <div className={styles["chart_" + chartTypes[chart]]}>
        {(chart === 1 || chart === 4 || chart === 5) && (
          <div className={styles.chartcontrols}>
            {order.map((team, i) => (
              <React.Fragment key={i}>
                {team.map((pid, j) => (
                  <div
                    key={j}
                    className={
                      styles[
                        active.charAt(pid) === "0" ? "greyout" : "control" + pid
                      ]
                    }
                    onClick={() => toggleChamp(pid)}
                  >
                    <Champ type="champ32b" id={match.champion[pid]} />
                  </div>
                ))}
                <div className={styles.space12}></div>
              </React.Fragment>
            ))}
          </div>
        )}

        {chart === 2 && (
          <div className={styles.teamlegend}>
            <span className={styles.winning}>Winner</span>{" "}
            <span className={styles.losing}>Loser</span>
          </div>
        )}
        {chart === 3 && (
          <div className={styles.teamlegend}>
            <span className={styles.winning}>Winner</span>
          </div>
        )}
        <div ref={chartDiv}></div>
      </div>
      <div ref={graphtip}></div>
    </div>
  );
};
export default MatchChart;
